import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchCadeVotedDecisions, fetchCadeVotes } from '@/services/cade';
import { Stat } from '@/pages/cade/Stat';
import { WindowCaption } from '@/pages/cade/WindowCaption';
import type {
  CadeCoParticipation,
  CadeDissentInstance,
  CadePairRecord,
  CadeVotedDecision,
  YearWindow,
} from '@/services/types';

//: Decisions loaded per batch of the index below.
const DECISIONS_PAGE_SIZE = 25;

/**
 * How CADE's conselheiros were minuted together -- and why that is not agreement.
 *
 * The screen behind the overview's agreement refusal. It exists because "the
 * sample is too small for a matrix" is a true sentence that leaves a reader with
 * nowhere to see what IS recorded, and what is recorded is real.
 *
 * The whole design is one decision: counts, never a rate. The breakdown columns
 * sit beside the shared-decision count and are never divided by it, because the
 * recorded vocabulary describes each vote's relation to the OUTCOME rather than
 * to the other member. Two conselheiros both minuted as outvoted were on the
 * same side of that decision; that is not the same as either persuading the
 * other, and the largest pair here is 29 such rows out of 30.
 *
 * The rate is refused on this page too, with the measurement rather than the
 * assertion -- see `analytics/cade/votes.py`.
 */
export function CadeVotesPage() {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'votes'],
    queryFn: fetchCadeVotes,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:votes.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:votes.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:votes.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/votes" />
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:votes.reach.title')}
        </h2>
        <Answer answer={data.co_participation} help={t('cade:votes.reach.help')}>
          {(record) => <ReachPanel record={record} />}
        </Answer>
        <div className="mt-2">
          <WindowCaption window={data.as_of.year_window} />
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:votes.guide.reach" />
        </div>
      </section>

      {/* The same panel and the same label prefix the overview uses, so the five
          position names are authored once. */}
      <OutcomeMixPanel
        answer={data.positions}
        titleKey="cade:votes.positions.title"
        helpKey="cade:votes.positions.help"
        labelPrefix="cade:overview.positions."
      />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:votes.rate.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:votes.rate.intro')}</p>
        {/* `elsewhere`, not a bare refusal. This is the case the prop exists
            for: what is withheld is the RATE, while the pair counts it would be
            derived from render immediately below. Without it `Answer` finds no
            reason node to show and falls through to `EmptyState` -- "Nada a
            exibir" printed directly above a populated table, the same
            contradiction removed from `/cade/activity`. */}
        <Answer answer={data.agreement_rate} elsewhere={t('cade:votes.rate.elsewhere')}>
          {() => null}
        </Answer>
      </section>

      {/* NOT a second <Answer> on `co_participation`: it is already rendered as
          one above, and passing it to two printed the badge and reason twice on
          a refusal. The table just has nothing to draw when there is no value. */}
      {data.co_participation.value ? <PairTable record={data.co_participation.value} /> : null}

      <DissentList items={data.dissent_instances} />

      <VotedDecisionsList window={data.as_of.year_window} />
    </div>
  );
}

/**
 * The decisions behind "decisions with a vote" -- the index of the decision
 * pages, newest archive year first, loaded in batches as the reader scrolls.
 *
 * Its own query: the page's other panels are corpus-level and identical on
 * every batch, so they are not refetched as this list grows.
 */
function VotedDecisionsList({ window }: { window: YearWindow | null | undefined }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cade', 'votes', 'decisions'],
      queryFn: ({ pageParam }) =>
        fetchCadeVotedDecisions({ limit: DECISIONS_PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      // Driven by the server's own total, never by "the last page came back
      // full" -- a result set that is an exact multiple of the page size would
      // otherwise leave a button promising rows that do not exist.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
    });
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:votes.decisions.title')}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t('cade:votes.decisions.help')}</p>
      </div>
      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/votes/decisions" />
      </div>

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <>
          <p className="text-xs text-muted-foreground" aria-live="polite" data-total={total}>
            {t('cade:votes.decisions.showing', {
              loaded: number.format(items.length),
              total: number.format(total),
            })}
          </p>
          <WindowCaption window={window} />
          <div
            ref={scrollRef}
            className="max-h-[60vh] overflow-auto rounded-lg border"
            role="region"
            aria-label={t('cade:votes.decisions.tableLabel')}
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cade:votes.decisions.columns.decision')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:votes.decisions.columns.year')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:votes.decisions.columns.relator')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:votes.decisions.columns.outcome')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:votes.decisions.columns.votes')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('cade:votes.decisions.columns.split')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <VotedDecisionRow key={item.decision_key} item={item} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <div className="flex justify-center py-3">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {isFetchingNextPage
                    ? t('cade:cases.loadingMore')
                    : t('cade:votes.decisions.loadMore', { count: DECISIONS_PAGE_SIZE })}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('cade:votes.decisions.allLoaded', { total: number.format(total) })}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function VotedDecisionRow({ item }: { item: CadeVotedDecision }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className="px-3 py-2">
        <Link
          to={`/cade/votes/d/${item.decision_key}`}
          className="tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {item.case_number}
        </Link>
        {item.item_number ? (
          <span className="ml-2 text-muted-foreground">
            {t('cade:case.item', { item: item.item_number })}
          </span>
        ) : null}
        {item.is_final_observed ? (
          <span className="ml-2 whitespace-nowrap rounded border px-1.5 py-0.5 text-muted-foreground">
            {t('cade:case.final')}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">{item.session_year ?? '—'}</td>
      <td className="px-3 py-2">
        {item.relator && item.relator_slug ? (
          <Link to={`/cade/r/${item.relator_slug}`} className="text-primary underline-offset-4 hover:underline">
            {item.relator}
          </Link>
        ) : (
          <span className="text-muted-foreground">{item.relator ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {(item.outcome_codes.length === 0 ? ['UNCLASSIFIED'] : item.outcome_codes)
          .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
          .join(', ')}
      </td>
      {/* Rows as minuted / people after the collapse, the same two figures the
          reach panel above divides by. */}
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {number.format(item.votes)}
        {item.people !== item.votes ? (
          <span className="text-muted-foreground"> / {number.format(item.people)}</span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {[
          item.is_unanimous === true ? t('cade:decisions.hit.unanimous') : null,
          item.is_unanimous === false ? t('cade:decisions.hit.byMajority') : null,
          item.dissenting > 0 ? t('cade:votes.decisions.dissenting', { count: item.dissenting }) : null,
          item.recused > 0 ? t('cade:votes.decisions.recused', { count: item.recused }) : null,
        ]
          .filter(Boolean)
          .join(' · ') || '—'}
      </td>
    </tr>
  );
}

/** The denominators, before any pair is shown. */
function ReachPanel({ record }: { record: CadeCoParticipation }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* The ratio, not the raw count. `votes` is what the minutes RECORD;
            every pair figure on this page is built from `votes_collapsed`, one
            row per person per decision. Printing 350 alone put a headline over a
            table computed from 330, and the two rungs between them are real: 2
            votes resolve to no canonical name and 18 are a second row for
            someone already minuted on that decision. Same `X / Y` shape as the
            two stats beside it, so the reach reads at a glance rather than only
            in the note below. */}
        <Stat
          label={t('cade:votes.stats.votes')}
          value={`${number.format(record.votes_collapsed)} / ${number.format(record.votes)}`}
          hint={t('cade:overview.corpus.canonicalHint', { count: record.votes_canonical })}
        />
        <Stat
          label={t('cade:votes.stats.withVotes')}
          value={`${number.format(record.decisions_with_votes)} / ${number.format(record.decisions)}`}
          hint={percent.format(record.vote_coverage)}
        />
        <Stat
          label={t('cade:votes.stats.pairable')}
          value={`${number.format(record.decisions_pairable)} / ${number.format(record.decisions)}`}
        />
        <Stat label={t('cade:votes.stats.people')} value={number.format(record.people)} />
      </dl>
      <p className="text-muted-foreground">
        {t('cade:votes.reach.coverage', {
          pairable: number.format(record.decisions_pairable),
          decisions: number.format(record.decisions),
          share: percent.format(record.pair_coverage),
        })}
      </p>
      {record.recused_co_minutings > 0 ? (
        <p className="text-muted-foreground">
          {t('cade:votes.reach.recusals', {
            excluded: number.format(record.recused_co_minutings),
            mutual: number.format(record.mutual_recusals),
            recused: number.format(record.recused_votes),
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The pairs, ordered by shared decisions.
 *
 * The count is emphasised and the breakdown is muted, exactly as ANATEL's
 * Acórdãos table emphasises dissent counts over recorded appearances: the
 * columns beside the count are context, never a denominator the reader is
 * invited to divide by.
 */
function PairTable({ record }: { record: CadeCoParticipation }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:votes.pairs.title')}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t('cade:votes.pairs.intro')}</p>
      </div>

      {record.pairs.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('cade:votes.pairs.none')}</p>
      ) : (
        <div
          className="overflow-x-auto rounded-lg border"
          role="region"
          aria-label={t('cade:votes.pairs.tableLabel')}
        >
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('cade:votes.columns.pair')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('cade:votes.columns.shared')}
                </th>
                <th className="px-3 py-2 font-medium">{t('cade:votes.columns.years')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('cade:votes.columns.outvoted')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('cade:votes.columns.withRapporteur')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('cade:votes.columns.split')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('cade:votes.columns.dissent')}
                </th>
              </tr>
            </thead>
            <tbody>
              {record.pairs.map((pair) => (
                <PairRow key={`${pair.name_a_slug}-${pair.name_b_slug}`} pair={pair} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {record.below_floor > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('cade:votes.pairs.belowFloor', {
            below: number.format(record.below_floor),
            total: number.format(record.total_pairs),
            threshold: number.format(record.threshold),
          })}
        </p>
      ) : null}

      <FieldGuide i18nKey="cade:votes.guide.columns" />
    </section>
  );
}

/**
 * Every recorded dissent, one row per minuted instance.
 *
 * The pair table can say a pair was "with dissent" but never WHICH decisions;
 * this is the list a lawyer reads one by one. It is a review list by design --
 * the recorded vocabulary is all this corpus has (there is no per-case nominal
 * call behind it), so a count of these rows is not a consistency measure and
 * the section says so rather than inviting the inference.
 */
function DissentList({ items }: { items: CadeDissentInstance[] }) {
  const { t } = useTranslation(['cade']);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:votes.dissents.title')}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('cade:votes.dissents.intro', { count: items.length })}
        </p>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('cade:votes.dissents.none')}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={`${item.case_key}-${item.item_number ?? ''}-${item.voter_slug}-${item.position}`}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded border p-2.5 text-xs"
            >
              {/* The DECISION page, not the case page: a dissent is read on the
                  decision it was minuted on, with every other vote beside it. */}
              <Link
                to={`/cade/votes/d/${item.decision_key}`}
                className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
              >
                {item.case_number}
              </Link>
              {item.item_number ? (
                <span className="text-muted-foreground">{t('cade:case.item', { item: item.item_number })}</span>
              ) : null}
              {item.session_year !== null ? (
                <span className="tabular-nums text-muted-foreground">{item.session_year}</span>
              ) : null}
              <Link
                to={`/cade/r/${item.voter_slug}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {item.voter}
              </Link>
              <span className="rounded border px-1.5 py-0.5 text-muted-foreground">
                {t(`cade:overview.positions.${item.position}`, { defaultValue: item.position })}
              </span>
              {item.session_date ? (
                <span className="text-muted-foreground">{item.session_date}</span>
              ) : null}
              {item.outcome_codes.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {item.outcome_codes.map((code) => (
                    <span
                      key={code}
                      className="rounded border border-dashed px-1.5 py-0.5 text-muted-foreground"
                    >
                      {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
                    </span>
                  ))}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PairRow({ pair }: { pair: CadePairRecord }) {
  const { i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className="px-3 py-2">
        <Link
          to={`/cade/r/${pair.name_a_slug}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {pair.name_a}
        </Link>
        <span className="text-muted-foreground"> · </span>
        <Link
          to={`/cade/r/${pair.name_b_slug}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {pair.name_b}
        </Link>
      </td>
      {/* Emphasised: this is the figure the table is ordered by and the only one
          that means anything on its own. */}
      <td className="px-3 py-2 text-right font-medium tabular-nums text-foreground">
        {number.format(pair.shared)}
      </td>
      {/* The window of the count beside it: 30 shared decisions across one
          archive year and across eight are different facts. */}
      <td className="px-3 py-2 tabular-nums text-muted-foreground">
        {pair.first_year === null || pair.last_year === null
          ? '—'
          : pair.first_year === pair.last_year
            ? String(pair.first_year)
            : `${pair.first_year}–${pair.last_year}`}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(pair.both_outvoted)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(pair.both_with_rapporteur)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(pair.split)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(pair.dissent_involved)}
      </td>
    </tr>
  );
}

